import { inject, Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { of, ReplaySubject, timer } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { clientConfig, setClientConfig } from 'src/client-config';
import { AppContext } from '../app/context';
import { AuthService, clearAuthStateFromStorage, parseJwt } from '../auth/auth.service';
import { isCanvasRendererHost } from '../canvas-renderer/canvas-renderer-host';
import { ExternalContentService } from '../external-content/external-content.service';
import { GainsightPX } from '../gainsightPX/gainsightPX.service';
import { DataModelResolver } from '../pbir/dataModel/DataModelResolver';
import { RichTextValue } from '../pbir/RichTextValue';
import { AppError, catchAppError } from '../shared/utils';
import { combineLatestRecord } from '../shared/utils/combineLatestRecord';
import { AppActions } from '../state/app';
import { AppCurrenciesDto, AppMaintenanceModeDto, AppService, ClientConfigDto, ClientCustomizationService, EnterpriseUserService, ItemCategoryService, LocaleService, PivotStudioService, ProductService, ScheduleService, UserAppAccessStatus, UserService, UserStateService } from './api.services';
import { logger } from '../pbir/utils/logger';

const LOGGER = logger('AppInitService');

@Injectable({ providedIn: 'root' })
export class AppInitService {
  readonly dataModelResolver = inject(DataModelResolver);

  constructor(
    private readonly router: Router,
    private readonly appService: AppService,
    private readonly authService: AuthService,
    private readonly userService: UserService,
    private readonly store: Store,
    private readonly externalContentService: ExternalContentService,
    private readonly userStateService: UserStateService,
    private readonly productService: ProductService,
    private readonly itemCategoryService: ItemCategoryService,
    private readonly pivotStudioService: PivotStudioService,
    private readonly scheduleService: ScheduleService,
    private readonly enterpriseUserService: EnterpriseUserService,
    private readonly clientCustomizationService: ClientCustomizationService,
    private readonly localeService: LocaleService,
    private readonly appContext: AppContext,
    private readonly domSanitizer: DomSanitizer,
    private readonly gainsightPX: GainsightPX,) {
    RichTextValue.domSanitizer = domSanitizer;
  }

  public route = '/';
  public result: AppInitResult = AppInitResult.Pending;
  public clientConfig: ClientConfigDto = clientConfig;
  public userAccess: UserAppAccessStatus = UserAppAccessStatus.Unauthorized;
  public maintenanceMode: AppMaintenanceModeDto = { active: false };
  
  private _initDone$ = new ReplaySubject<boolean>(1);

  get done$() {
    return this._initDone$;
  }

  async init(): Promise<AppInitResult> {
    this.route = window.location.pathname;

    let result = AppInitResult.Success;

    try {
      if (isCanvasRendererHost) {
        await this.loadApp();
      }
      else {
        await this.loadClientConfig();

        if (!await this.authService.init()) {
          result = AppInitResult.Unauthenticated;
        }
        else {
          if (!await this.checkRegion()) {
            result = AppInitResult.RegionRedirect;
          }
          else {
            await this.checkUserAccess();
            await this.checkForMaintenance();
            await this.loadApp();

            if (this.authService.shouldRedirect) {
              let redirectTo = this.authService.redirectTo ?? '/';
              this.router.navigateByUrl(redirectTo, { replaceUrl: true });
            }
          }
        }
      }
    }
    catch (err) {
      const errorMessage =
        err ? (
          err instanceof Object ? err.toString() :
            err instanceof Error ? err.message : '' + err) : 'undefined';

      const message = `Unexpected error thrown when initializing:\n${errorMessage}`;

      LOGGER.error(message, err);

      result = AppInitResult.Error;
    }
    finally {
      if (result !== AppInitResult.Success) {
        LOGGER.warn('Init completed unsuccessfully.', { result: AppInitResult[result] });

        if (result !== AppInitResult.RegionRedirect) {
          await this.authService.login();
        }
      }
      else {
        LOGGER.debug('Init completed.', { result: AppInitResult[result] });
      }

      this._initDone$.next(true);

      this.result = result;
    }

    return result;
  }

  private async loadClientConfig() {
    const clientConfigResponse = await fetch('/api/app/config', { method: 'GET', cache: 'no-store' });
    if (clientConfigResponse.status > 400)
      throw new Error(`Failed to get maintenance mode: ${clientConfigResponse.status}`);

    const clientConfig = await clientConfigResponse.json();

    LOGGER.debug(`Client config loaded.`, clientConfig);

    // INFO: Modules use client config from the client-config file, so instead of rewiring that, just set it from here
    setClientConfig(clientConfig);

    this.clientConfig = clientConfig;
  }

  private async checkRegion(): Promise<boolean> {
    try {
      const redirectUrl = await this.userService.getRegionRedirectUrl().toPromise();
      if (!redirectUrl) {
        return true;
      }

      if (window.location.origin.toLowerCase() == new URL(redirectUrl).origin.toLowerCase())
        return true;

      LOGGER.warn(`Retrieved redirectURL: '${redirectUrl}`);

      try {
        clearAuthStateFromStorage();
        // await (window as any).InsightsIdpLogoutCallback();
      }
      finally {
        setTimeout(() => window.location.href = redirectUrl);
        return false;
      }
    }
    catch (err) {
      let redirectUrl = this.tryGetRedirectUrlFromAccessToken();

      try {
        clearAuthStateFromStorage();
        // await (window as any).InsightsIdpLogoutCallback();
      }
      finally {
        if (redirectUrl) {
          setTimeout(() => window.location.href = redirectUrl);
        }
        else {
          throw new AppError({
            status: 401,
            message: `Could not fetch region redirect URL`
          });
        }
      }
    }
  }

  private tryGetRedirectUrlFromAccessToken() {
    try {
      let jwt = parseJwt(this.authService.accessToken);
      if ('insights:region' in jwt) {
        let region = jwt['insights:region']?.toLowerCase();
        if (region == 'eu') {
          return 'https://eu1.optimize.trgscreen.com';
        }
        else if (region == 'apac') {
          return 'https://ap1.optimize.trgscreen.com';
        }
        if (region == 'us') {
          return 'https://optimize.trgscreen.com';
        }
      }
    }
    catch (err) {
      console.error(err);
    }

    return null;
  }

  private async checkUserAccess() {
    const userAccess = await this.appService.getUserAccess().toPromise();

    LOGGER.debug(`User Access status loaded.`, { userAccess: UserAppAccessStatus[userAccess] });

    this.userAccess = userAccess;
  }

  private async checkForMaintenance() { 
    const maintenanceModeResponse = await fetch('/api/app/maintenance', { method: 'GET', cache: 'no-store' });
    if (maintenanceModeResponse.status > 400)
      throw new Error(`Failed to get maintenance mode: ${maintenanceModeResponse.status}`);

    const maintenanceMode = await maintenanceModeResponse.json();

    LOGGER.debug(`Maintenance Mode state loaded.`, maintenanceMode);

    this.maintenanceMode = maintenanceMode;
  }

  public async loadApp() {
    return this.userService.get().pipe(
      switchMap(user => combineLatestRecord({
        userClaims: this.userService.getClaims(),

        enterpriseUserData: this.enterpriseUserService.getData(),
        userState: this.userStateService.getUserState(),

        settings: this.appService.getSettings(),
        features: this.appService.getFeatures(),
        
        products: this.productService.listProducts(),
        itemCategories: this.itemCategoryService.listCategories(),

        schedulerConfig: this.scheduleService.getConfig(),

        walkMeParameters: this.appService.getWalkMeParameters(),
        externalScripts: this.appService.getExternalScripts(),

        clientCustomization: this.clientCustomizationService.getUserClientCustomization().pipe(
          catchAppError({ showErrorSnack: false })
        ),
        locales: this.localeService.getList(),

        customFieldFunctions: this.pivotStudioService.getCustomFieldFunctions(),
        
      	gainsightPX: this.gainsightPX.init(),
      }).pipe(
        tap(data => {
          let initData = {
            user,
            userProfileClaims: this.authService.claims,
            ...data
          };

          this.appContext.init(initData);

	        this.gainsightPX.setGlobals();
	        this.gainsightPX.setUser(user, initData.userClaims, initData.enterpriseUserData);

          let { walkMeParameters, externalScripts } = data;
          this.externalContentService.addWalkMeVariables(walkMeParameters);
          this.externalContentService.appendExternalScripts(externalScripts);

          this.store.dispatch(AppActions.loadSuccess(initData));

          LOGGER.debug(`Loaded App`);
        }),
        catchAppError(error => this.store.dispatch(AppActions.loadError({ error })))
      ))).toPromise();
  }
}

export enum AppInitResult {
  Pending,
  Success,
  Unauthenticated,
  Error,
  RegionRedirect
}
